import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { withStyles } from '@material-ui/core/styles'
import { Paper, Typography, CircularProgress, Card, CardContent, Divider } from '@material-ui/core'
import DashboardLayout from '../../layouts/dashboard'
import axiosInstance from 'stores/axios'
import { Portlet, PortletContent } from '../../components'
import uiStore from '../../stores/ui-store'
import Markdown from 'react-markdown'
import { GetText as $ } from 'stores/UITexts'

const styles = (theme: any) => ({
  root: {
    padding: theme.spacing(3),
  },
  container: {
    marginTop: theme.spacing(3),
  },
  portlet: {
    height: '100%',
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  noData: {
    textAlign: 'center' as 'center',
    padding: theme.spacing(3),
  },
})

interface Props {
  classes: any
}

@observer
class AIAnalysis extends Component<Props> {
  @observable analysisData: string | null = null
  @observable loading: boolean = true
  @observable error: string | null = null

  async componentDidMount() {
    try {
      await this.fetchAnalysisData()
    } catch (error) {
      console.error('Error fetching AI analysis data:', error)
      this.error = 'Failed to load AI analysis data'
      uiStore.showSnackbar('Failed to load AI analysis data')
    } finally {
      this.loading = false
    }
  }

  async fetchAnalysisData() {
    const response = await axiosInstance.get('tenant/ai-analysis')
    this.analysisData = response.data
  }

  renderContent() {
    const { classes } = this.props

    if (this.loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )
    }

    if (this.error) {
      return (
        <Paper className={classes.noData}>
          <Typography variant="h6" color="error">
            {this.error}
          </Typography>
        </Paper>
      )
    }

    if (!this.analysisData) {
      return (
        <Paper className={classes.noData}>
          <Typography variant="h6">No AI analysis data available.</Typography>
        </Paper>
      )
    }

    return (
      <div>
        <Card className={classes.card} key={'analysis-card'}>
          <CardContent>
            <Divider />
            <div style={{ width: '50%', marginLeft: '1.25%' }}>
              <Markdown
                components={{
                  h1: ({ className, children }) => (
                    <Typography
                      component="h1"
                      variant="h4"
                      gutterBottom
                      className={className}
                      style={{ marginBottom: 32, marginTop: 32, fontWeight: 'bold' }}
                    >
                      {children}
                    </Typography>
                  ),
                  h2: ({ className, children }) => (
                    <Typography component="h2" variant="h5" gutterBottom className={className} style={{ marginTop: 16 }}>
                      {children}
                    </Typography>
                  ),
                  h3: ({ className, children }) => (
                    <Typography component="h3" variant="h6" gutterBottom className={className} style={{ marginTop: 16 }}>
                      {children}
                    </Typography>
                  ),
                  p: ({ className, children }) => (
                    <Typography component="p" variant="body1" paragraph className={className} style={{ marginTop: 16 }}>
                      {children}
                    </Typography>
                  ),
                  ul: ({ node, ...props }) => <ul style={{ marginBottom: 16, paddingLeft: 20 }} {...props} />,
                  li: ({ node, ...props }) => (
                    <li style={{ marginBottom: 8, display: 'list-item' }}>
                      <Typography>{props.children}</Typography>
                    </li>
                  ),
                  // Render preformatted text blocks like paragraphs, preserving whitespace
                  pre: ({ children }) => (
                    <Typography component="div" variant="body1" paragraph style={{ whiteSpace: 'pre-wrap' }}>
                      {children}
                    </Typography>
                  ),
                  // Render inline code as normal text within a span
                  code: ({ children }) => <span className="code">{children}</span>,
                  table: ({ node, ...props }) => (
                    <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => <thead {...props} />,
                  tbody: ({ node, ...props }) => <tbody {...props} />,
                  tr: ({ node, ...props }) => <tr {...props} />,
                  th: ({ node, ...props }) => (
                    <th style={{ border: '1px solid #ddd', padding: 8, backgroundColor: '#f2f2f2', textAlign: 'left' }} {...props} />
                  ),
                  td: ({ node, ...props }) => <td style={{ border: '1px solid #ddd', padding: 8 }} {...props} />,
                  hr: ({ node, ...props }) => <Divider />, // Style horizontal rules
                  strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>, // Ensure bold text is rendered correctly
                }}
              >
                {this.analysisData}
              </Markdown>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  render() {
    const { classes } = this.props

    return (
      <DashboardLayout title={$('__ai_analysis.title__')}>
        <div className={classes.root}>
          <div className={classes.container}>
            <Portlet className={classes.portlet}>
              <PortletContent>{this.renderContent()}</PortletContent>
            </Portlet>
          </div>
        </div>
      </DashboardLayout>
    )
  }
}

export default withStyles(styles)(AIAnalysis)
